import type { CompetencyCode, KnowledgeCode, MisconceptionCode, Question } from '../types'
import { SeedGenerator } from './generators/math_generators'

type ExpLogSeed = {
  id: string
  initial: number
  multiplier: number
  periods: number
  safeFraction: number
  room: number
  hot: number
  observed: number
  observedTime: number
  target: number
  principal: number
  annualRate: number
  targetBalance: number
}

const option = (id: string, text: string) => ({ id, text })

const meta = (seed: ExpLogSeed, topic: string, competency: CompetencyCode[] = ['quantitative-reasoning', 'problem-solving']) => ({
  exam: 'VACT' as const,
  domain: 'Toán học',
  topic,
  knowledge: ['exponents-logarithms'] as KnowledgeCode[],
  competency,
  prerequisites: ['exponents-logarithms'] as KnowledgeCode[],
  misconceptionOptions: ['concept-gap', 'calculation-slip', 'strategy-choice', 'careless-reading'] as MisconceptionCode[],
  tags: ['original', 'authored-variant', 'k11', 'chapter-2', 'exponents-logarithms', 'conictypst-k11', 'parameterized'],
  aiContext: JSON.stringify(seed),
})

function choices(correct: string, alternatives: string[]) {
  const seen = new Set<string>()
  return [correct, ...alternatives].map((candidate, index) => {
    let value = candidate
    let suffix = 1
    while (seen.has(value)) value = `${candidate} (${suffix++})`
    seen.add(value)
    return option(['A', 'B', 'C', 'D'][index], value)
  })
}

export function generateExpLogSeed(id: string): ExpLogSeed {
  const gen = new SeedGenerator(id)
  const initial = [200, 250, 320, 400][gen.randomInt(0, 3)]
  const multiplier = [1.1, 1.12, 1.15, 1.2][gen.randomInt(0, 3)]
  const periods = [4, 5, 6, 8][gen.randomInt(0, 3)]
  const room = [18, 20, 22][gen.randomInt(0, 2)]
  const hot = [82, 86, 90][gen.randomInt(0, 2)]
  const observedTime = [5, 8, 10][gen.randomInt(0, 2)]
  const retainedOptions = [0.72, 0.64, 0.56]
  const retained = retainedOptions[gen.randomInt(0, retainedOptions.length - 1)]
  const observed = room + (hot - room) * retained
  const target = room + (hot - room) * [0.25, 0.3, 0.35][gen.randomInt(0, 2)]
  const principal = [80, 100, 120, 150][gen.randomInt(0, 3)]
  const annualRate = [0.06, 0.07, 0.08][gen.randomInt(0, 2)]
  const targetBalance = principal * [1.35, 1.4, 1.5][gen.randomInt(0, 2)]
  return { id, initial, multiplier, periods, safeFraction: [0.05, 0.08, 0.1][gen.randomInt(0, 2)], room, hot, observed, observedTime, target, principal, annualRate, targetBalance }
}

function growthQuestions(seed: ExpLogSeed): Question[] {
  const { initial: n0, multiplier: q, periods: n, safeFraction } = seed
  const result = n0 * q ** n
  const safePeriods = Math.ceil(Math.log(safeFraction) / Math.log(1 / q))
  const ratioPower = 3
  return [
    {
      ...meta(seed, 'Hàm mũ – tăng trưởng rời rạc', ['modelling', 'quantitative-reasoning', 'problem-solving']),
      id: `VACT-K11-EXP-GROWTH-${seed.id}-01`, difficulty: 4, cognitiveLevel: 'M2', type: 'single-choice',
      passageTitle: 'Tăng trưởng theo chu kì',
      passageText: `Một quần thể có quy mô ban đầu ${n0} đơn vị và sau mỗi chu kì được nhân với ${q}.`,
      stem: `Sau ${n} chu kì, quy mô theo mô hình là gần bằng`,
      choices: choices(result.toFixed(1), [(n0 * q * n).toFixed(1), (n0 * q ** (n - 1)).toFixed(1), (n0 * (1 + q) ** n).toFixed(1)]),
      answer: 'A',
      solution: `Mỗi chu kì nhân thêm cùng một hệ số ${q}, nên $N_n=${n0}\\cdot${q}^{${n}}=${result.toFixed(1)}$. Cộng tuyến tính $${n0}\\cdot${q}\\cdot${n}$ là mô hình sai.`,
      hint1: 'Tăng theo tỉ lệ cố định là nhân lặp, không phải cộng đều.',
      hint2: `Dùng công thức $N_n=N_0q^n$.`,
      estimatedSeconds: 125, variantGroup: 'k11-exp-growth-v1',
    },
    {
      ...meta(seed, 'Logarit – so sánh theo thang đo'),
      id: `VACT-K11-EXP-GROWTH-${seed.id}-02`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      stem: `Trên một thang đo logarit cơ số 10, một đại lượng lớn hơn đại lượng khác ${ratioPower} đơn vị. Tỉ số hai đại lượng bằng`,
      choices: choices(`${10 ** ratioPower}`, ['3', '30', `${10 ** (ratioPower - 1)}`]),
      answer: 'A',
      solution: `Nếu $\\log_{10}A-\\log_{10}B=${ratioPower}$ thì $\\log_{10}(A/B)=${ratioPower}$. Suy ra $A/B=10^{${ratioPower}}=${10 ** ratioPower}$.`,
      hint1: 'Hiệu logarit là logarit của thương.',
      hint2: 'Lấy lũy thừa cơ số 10 ở hai vế.',
      estimatedSeconds: 120, variantGroup: 'k11-exp-log-scale-v1',
    },
    {
      ...meta(seed, 'Hàm mũ – phân rã và ngưỡng an toàn', ['modelling', 'decision-making', 'problem-solving']),
      id: `VACT-K11-EXP-GROWTH-${seed.id}-03`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      stem: `Một chất còn lại theo tỉ lệ $(${(1 / q).toFixed(3)})^t$ sau t ngày. Cần ít nhất bao nhiêu ngày để lượng còn dưới ${(safeFraction * 100).toFixed(0)}% lượng ban đầu?`,
      choices: choices(`${safePeriods}`, [`${safePeriods - 1}`, `${safePeriods + 1}`, `${safePeriods + 2}`]),
      answer: 'A',
      solution: `Cần $(${(1 / q).toFixed(3)})^t<${safeFraction}$. Lấy logarit: $t>\\dfrac{\\ln ${safeFraction}}{\\ln ${(1 / q).toFixed(3)}}\\approx ${(Math.log(safeFraction) / Math.log(1 / q)).toFixed(2)}$. Vì t là số ngày nguyên, lấy tròn lên ${safePeriods}.`,
      hint1: 'Phân rã theo tỉ lệ cố định cũng là hàm mũ.',
      hint2: 'Sau khi lấy logarit, nhớ bất đẳng thức yêu cầu số ngày nguyên nhỏ nhất.',
      estimatedSeconds: 190, variantGroup: 'k11-exp-decay-threshold-v1',
    },
  ]
}

function coolingQuestions(seed: ExpLogSeed): Question[] {
  const { room, hot, observed, observedTime, target } = seed
  const retained = (observed - room) / (hot - room)
  const k = -Math.log(retained) / observedTime
  const timeToTarget = -Math.log((target - room) / (hot - room)) / k
  return [
    {
      ...meta(seed, 'Hàm mũ – định luật làm nguội', ['modelling', 'quantitative-reasoning', 'problem-solving']),
      id: `VACT-K11-EXP-COOL-${seed.id}-01`, difficulty: 5, cognitiveLevel: 'M3', type: 'single-choice',
      passageTitle: 'Làm nguội theo môi trường',
      passageText: `Nhiệt độ của một vật được mô hình bởi $T(t)=${room}+(${hot}-${room})e^{-kt}$. Sau ${observedTime} phút, nhiệt độ đo được là ${observed.toFixed(1)}°C.`,
      stem: 'Hệ số làm nguội k gần đúng bằng',
      choices: choices(k.toFixed(4), [(k / 2).toFixed(4), (k * 2).toFixed(4), (retained / observedTime).toFixed(4)]),
      answer: 'A',
      solution: `Thay dữ kiện vào: $${observed.toFixed(1)}-${room}=(${hot}-${room})e^{-k\\cdot${observedTime}}$, nên $e^{-${observedTime}k}=${retained.toFixed(2)}$. Do đó $k=-\\ln(${retained.toFixed(2)})/${observedTime}\\approx${k.toFixed(4)}$.`,
      hint1: 'Trừ nhiệt độ môi trường trước để cô lập phần mũ.',
      hint2: `Tỉ lệ còn lại là (${observed.toFixed(1)}−${room})/(${hot}−${room}).`,
      estimatedSeconds: 225, variantGroup: 'k11-exp-cooling-v1',
    },
    {
      ...meta(seed, 'Hàm mũ – định luật làm nguội', ['modelling', 'decision-making', 'problem-solving']),
      id: `VACT-K11-EXP-COOL-${seed.id}-02`, difficulty: 5, cognitiveLevel: 'M3', type: 'single-choice',
      stem: `Theo mô hình trên, vật lần đầu đạt ${target.toFixed(1)}°C sau khoảng`,
      choices: choices(`${timeToTarget.toFixed(1)} phút`, [`${(timeToTarget - 2).toFixed(1)} phút`, `${(timeToTarget + 2).toFixed(1)} phút`, `${(timeToTarget * 2).toFixed(1)} phút`]),
      answer: 'A',
      solution: `Giải $${target.toFixed(1)}=${room}+(${hot}-${room})e^{-kt}$ được $t=-\\dfrac{\\ln\\left((${target.toFixed(1)}-${room})/(${hot}-${room})\\right)}{${k.toFixed(4)}}\\approx${timeToTarget.toFixed(1)}$ phút.`,
      hint1: 'Dùng k vừa tìm, sau đó cô lập $e^{-kt}$.',
      hint2: 'Chỉ nhận nghiệm dương vì thời gian sau lúc bắt đầu làm nguội.',
      estimatedSeconds: 225, variantGroup: 'k11-exp-cooling-v1',
    },
  ]
}

function financeQuestions(seed: ExpLogSeed): Question[] {
  const { principal: p, annualRate: r, targetBalance: target } = seed
  const n = Math.ceil(Math.log(target / p) / Math.log(1 + r))
  const before = p * (1 + r) ** (n - 1)
  const after = p * (1 + r) ** n
  return [
    {
      ...meta(seed, 'Lãi kép – ngưỡng số năm tối thiểu', ['modelling', 'decision-making', 'problem-solving']),
      id: `VACT-K11-EXP-FINANCE-${seed.id}-01`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      passageTitle: 'Tiết kiệm lãi kép',
      passageText: `Gửi ${p} triệu đồng, lãi suất kép ${(r * 100).toFixed(0)}%/năm, không nộp thêm tiền.`,
      stem: `Sau ít nhất bao nhiêu năm số dư vượt ${target.toFixed(0)} triệu đồng?`,
      choices: choices(`${n} năm`, [`${n - 1} năm`, `${n + 1} năm`, `${n + 2} năm`]),
      answer: 'A',
      solution: `Số dư sau n năm là $S_n=${p}(1+${r})^n$. Ta có $S_{${n - 1}}\\approx${before.toFixed(1)}$ chưa vượt ${target.toFixed(0)}, còn $S_{${n}}\\approx${after.toFixed(1)}$ đã vượt. Vậy mốc nhỏ nhất là ${n} năm.`,
      hint1: 'Dùng lãi kép, không dùng lãi đơn.',
      hint2: 'Có thể giải bằng logarit rồi làm tròn lên, hoặc kiểm hai số nguyên kề nhau.',
      estimatedSeconds: 185, variantGroup: 'k11-exp-finance-v1',
    },
    {
      ...meta(seed, 'Bất phương trình logarit – điều kiện xác định', ['logical-reasoning', 'problem-solving']),
      id: `VACT-K11-EXP-FINANCE-${seed.id}-02`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      stem: 'Tập nghiệm của bất phương trình $\\log_2(x-3)\\le4$ là',
      choices: choices('$(3;19]$', ['$(3;16]$', '$(-\\infty;19]$', '$[3;19]$']),
      answer: 'A',
      solution: `Điều kiện là $x>3$. Vì cơ số 2 lớn hơn 1, bất phương trình tương đương $x-3\\le2^4=16$, tức $x\\le19$. Kết hợp điều kiện được $(3;19]$.`,
      hint1: 'Phải ghi điều kiện của logarit trước.',
      hint2: 'Cơ số lớn hơn 1 nên chiều bất phương trình giữ nguyên.',
      estimatedSeconds: 145, variantGroup: 'k11-log-inequality-v1',
    },
  ]
}

/** 9 difficult-enough, source-derived patterns per deterministic seed. */
/** Seven non-routine, source-inspired M2–M3 items for one deterministic seed. */
export function k11ExpLogQuestions(seed: ExpLogSeed): Question[] {
  return [...growthQuestions(seed), ...coolingQuestions(seed), ...financeQuestions(seed)]
}
